import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { Box, Button } from '@mui/material';
import { AdCreationProgress } from 'components/common';
import type { ReuploadCreativeRef, ReuploadCreativesResponse } from 'features/creatives';
import { useReuploadCreatives } from 'features/creatives';
import { JobResultTable } from 'features/facebook';
import { useEnqueueSnackbar, useJobStatusCheck, useLocationParams } from 'hooks';
import { getApiErrorMessage } from 'lib/api';
import datadogActions from 'lib/datadog';
import { useGlobalSettings } from 'providers';
import AddCreativesDialog from './AddCreativesDialog';
import XCreativesGrid from './XCreativesGrid';

const bottomBarSx = {
  position: 'sticky',
  bottom: 0,
  zIndex: 5,
  backgroundColor: 'white',
  display: 'flex',
  justifyContent: 'flex-end',
  py: 1.5,
} as const;

const UploadCreatives: FC = () => {
  const { selectedApp } = useGlobalSettings();
  const { onShowErrorAlert } = useEnqueueSnackbar();
  const { setFlatCreatives } = useLocationParams();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    runJubStatusCheck,
    isLoading: isJobStatusChecking,
    isFinished: isJobStatusFinished,
    clearJobStatus,
    data: jobStatusResponse,
  } = useJobStatusCheck();

  const { mutate, isPending } = useReuploadCreatives({
    config: {
      onSuccess: ({ job_id }: ReuploadCreativesResponse) => {
        setIsDialogOpen(false);
        setFlatCreatives([]);
        runJubStatusCheck(job_id);
      },
      onError: (err) => {
        datadogActions.trackDatadogError(err, { source: 'twitter_upload_creatives_reupload_error' });
        onShowErrorAlert(getApiErrorMessage(err, 'Failed to reupload creatives'), { delayAlert: 3500 });
      },
    },
  });

  const handleUpload = useCallback(
    (refs: ReuploadCreativeRef[]) => {
      if (!selectedApp) return;
      mutate({ app: selectedApp, creatives: refs });
    },
    [mutate, selectedApp],
  );

  const handleOpenDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  if (isJobStatusChecking) {
    return (
      <Box sx={{ display: 'flex', width: '100%', height: '100%', flex: 1 }}>
        <AdCreationProgress onLaunchMoreAds={clearJobStatus} message="Uploading in progress." />
      </Box>
    );
  }

  if (isJobStatusFinished) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', width: '100%' }}>
        <JobResultTable rows={jobStatusResponse?.result} />
        <Box sx={bottomBarSx}>
          <Button variant="text" color="primary" onClick={clearJobStatus}>
            Restart
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', width: '100%' }}>
      <XCreativesGrid key={selectedApp} app={selectedApp} />
      <Box sx={bottomBarSx}>
        <Button variant="contained" disabled={!selectedApp} onClick={handleOpenDialog}>
          Add creatives
        </Button>
      </Box>
      <AddCreativesDialog
        open={isDialogOpen}
        app={selectedApp}
        isUploading={isPending}
        onClose={handleCloseDialog}
        onUpload={handleUpload}
      />
    </Box>
  );
};

export default UploadCreatives;
